import { useState, useEffect } from "react";
import { api, IMAGES } from "../lib/api";
import { Instagram, Youtube, Twitter, Mail, MapPin, Send, Music, ArrowUpRight, Facebook } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

const DiscordGlyph = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={`fill-current ${className || ""}`} style={style} aria-hidden="true">
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.2.358-.43.84-.59 1.226a18.27 18.27 0 0 0-5.487 0A12.6 12.6 0 0 0 9.882 3a19.74 19.74 0 0 0-3.76 1.369C2.692 9.07 1.79 13.65 2.24 18.166a19.93 19.93 0 0 0 6.07 3.07c.49-.67.93-1.39 1.3-2.15a12.86 12.86 0 0 1-2.05-.99c.17-.13.34-.26.5-.4a14.2 14.2 0 0 0 11.88 0c.17.14.33.27.5.4-.66.39-1.34.72-2.05.99.37.76.81 1.48 1.3 2.15a19.9 19.9 0 0 0 6.07-3.07c.53-5.25-.9-9.79-3.94-13.797ZM8.02 15.331c-1.18 0-2.157-1.08-2.157-2.41 0-1.33.96-2.41 2.157-2.41 1.2 0 2.17 1.09 2.157 2.41 0 1.33-.96 2.41-2.157 2.41Zm7.96 0c-1.18 0-2.157-1.08-2.157-2.41 0-1.33.96-2.41 2.157-2.41 1.2 0 2.17 1.09 2.157 2.41 0 1.33-.95 2.41-2.157 2.41Z" />
  </svg>
);

const SOCIAL = [
  { Icon: Instagram, label: "INSTAGRAM", url: "https://www.instagram.com/syndafam", handle: "@syndafam", color: "#E63946" },
  { Icon: Music, label: "TIKTOK", url: "https://www.tiktok.com/@syndafam", handle: "@syndafam", color: "#E63946" },
  { Icon: Youtube, label: "YOUTUBE", url: "https://youtube.com/@syndafam", handle: "@syndafam", color: "#FCA311" },
  { Icon: Twitter, label: "X / TWITTER", url: "https://x.com/syndafam", handle: "@syndafam", color: "#FCA311" },
  { Icon: DiscordGlyph, label: "DISCORD", url: "https://discord.com/invite/zbaAxtqCv", handle: "join the server", color: "#5865F2" },
  { Icon: Facebook, label: "FACEBOOK", url: "https://www.facebook.com/syndafam", handle: "@syndafam", color: "#1877F2" },
];

export default function Connect() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  // Load (or reload) the TikTok embed script so the creator widget renders.
  useEffect(() => {
    const existing = document.querySelector('script[data-syndafam-tiktok="1"]');
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.src = "https://www.tiktok.com/embed.js";
    script.async = true;
    script.dataset.syndafamTiktok = "1";
    document.body.appendChild(script);
    return () => {
      try { script.remove(); } catch (err) {
        console.warn("TikTok script cleanup skipped", err);
      }
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Fill in name, email, and message");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message landed. We'll hit you back.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="connect-page">
      <Seo
        title="Connect | Contact SyndaFam — Toronto Hip-Hop Bookings & Press"
        description="Contact SyndaFam for bookings, press, features and collaborations. Follow the independent Canadian hip-hop collective on Instagram, YouTube, TikTok, X and Discord."
        path="/connect"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "url": "https://syndafam.com/connect",
          "name": "Contact SyndaFam",
          "about": { "@id": "https://syndafam.com/#organization" }
        }}
      />
      {/* Living aurora backdrop + drifting orbs across the whole page */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-70" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 620, height: 620, top: "-160px", left: "-180px" }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 360, height: 360, top: "45%", left: "40%", opacity: 0.18, animationDelay: "-3s" }} />
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[80vh] flex flex-col justify-end">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.95)), url(${IMAGES.connect})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pb-20 pt-32 w-full">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-4 text-glow-red reveal reveal-1">// LINK UP</div>
          <h1 className="font-display leading-[0.85] tracking-tighter reveal reveal-2" style={{ fontSize: "clamp(3rem, 11vw, 11rem)" }}>
            PULL UP.<br/><span className="text-[#E63946] text-glow-red">LINK UP.</span>
          </h1>
          <div className="mt-8 max-w-xl glass-card-strong rounded-3xl p-6 sm:p-7 reveal reveal-3">
            <p className="text-white/85 text-base sm:text-lg leading-relaxed">
              Press inquiries, bookings, collabs, or just want to say what&apos;s good — we read everything.
            </p>
          </div>
        </div>
      </section>

      {/* TIKTOK EMBED */}
      <section className="py-16" data-testid="tiktok-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-2">// LIVE FROM TIKTOK</div>
              <h2 className="font-display text-4xl sm:text-5xl">FROM THE FOR-YOU PAGE.</h2>
            </div>
            <a
              href="https://www.tiktok.com/@syndafam"
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs tracking-widest text-white/60 hover:text-[#E63946]"
              data-testid="tiktok-channel-link"
            >
              @SYNDAFAM ON TIKTOK →
            </a>
          </div>

          <div className="glass-card-strong rounded-3xl p-4 sm:p-6 flex justify-center float-card" data-testid="tiktok-embed-container">
            <blockquote
              className="tiktok-embed"
              cite="https://www.tiktok.com/@syndafam"
              data-unique-id="syndafam"
              data-embed-type="creator"
              style={{ maxWidth: 780, minWidth: 288, width: "100%" }}
            >
              <section>
                <a target="_blank" rel="noreferrer" href="https://www.tiktok.com/@syndafam?refer=creator_embed">
                  @syndafam
                </a>
              </section>
            </blockquote>
          </div>
        </div>
      </section>

      {/* SOCIAL CTAs — 2×2 grid on md+. Each tile keeps its own colour
          identity (Instagram purple/red gradient, YouTube red, X white,
          Discord blurple) so the row still reads like four separate
          brand pull-ins rather than a homogenised card wall. */}
      <section className="py-16" data-testid="social-grid-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="mb-8">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-2">// ACROSS THE FEED</div>
            <h2 className="font-display text-4xl sm:text-5xl">FOLLOW THE FAM EVERYWHERE.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {/* INSTAGRAM */}
            <a
              href="https://www.instagram.com/syndafam"
              target="_blank"
              rel="noreferrer"
              className="group block relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card h-full"
              data-testid="instagram-cta"
            >
              <div className="absolute inset-0 diagonal-stripes opacity-20 pointer-events-none" />
              <div className="orb orb-red pointer-events-none" style={{ width: 260, height: 260, top: -70, left: -70, opacity: 0.35 }} />
              <div className="relative p-7 sm:p-8 flex flex-col h-full gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-gradient-to-br from-[#FCA311] via-[#E63946] to-[#7B1FA2] p-[3px]">
                    <div className="w-full h-full rounded-[calc(1rem-3px)] bg-[#0A0A0A] flex items-center justify-center">
                      <Instagram className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/40">INSTAGRAM</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl leading-none">@SYNDAFAM</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">Behind-the-scenes, drop teasers, studio sessions, the family. Tap to follow.</p>
                </div>
                <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-white/40">instagram.com/syndafam</span>
                  <span className="font-display text-sm sm:text-base rounded-full bg-[#E63946] group-hover:bg-[#C92A36] text-white px-5 py-2.5 inline-flex items-center gap-2 transition-all shadow-[0_0_24px_rgba(230,57,70,0.55)] group-hover:-translate-y-px">
                    FOLLOW <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>

            {/* YOUTUBE */}
            <a
              href="https://youtube.com/@syndafam"
              target="_blank"
              rel="noreferrer"
              className="group block relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card h-full"
              data-testid="youtube-cta"
            >
              <div className="absolute inset-0 diagonal-stripes opacity-20 pointer-events-none" />
              <div className="orb orb-red pointer-events-none" style={{ width: 260, height: 260, top: -70, right: -70, opacity: 0.35 }} />
              <div className="relative p-7 sm:p-8 flex flex-col h-full gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-[#E63946] flex items-center justify-center shadow-[0_0_24px_rgba(230,57,70,0.55)]">
                    <Youtube className="w-8 h-8 text-white" />
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/40">YOUTUBE</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl leading-none">@SYNDAFAM</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">Music videos, vlogs, live cuts, full sessions. Hit subscribe and stay locked.</p>
                </div>
                <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-white/40">youtube.com/@syndafam</span>
                  <span className="font-display text-sm sm:text-base rounded-full bg-[#E63946] group-hover:bg-[#C92A36] text-white px-5 py-2.5 inline-flex items-center gap-2 transition-all shadow-[0_0_24px_rgba(230,57,70,0.55)] group-hover:-translate-y-px">
                    SUBSCRIBE <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>

            {/* X / TWITTER */}
            <a
              href="https://x.com/syndafam"
              target="_blank"
              rel="noreferrer"
              className="group block relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card h-full"
              data-testid="x-cta"
            >
              <div className="absolute inset-0 diagonal-stripes opacity-25 pointer-events-none" />
              <div className="orb orb-cyan pointer-events-none" style={{ width: 250, height: 250, top: -50, left: "45%", opacity: 0.25 }} />
              <div className="relative p-7 sm:p-8 flex flex-col h-full gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-black border border-white/30 flex items-center justify-center shadow-[0_0_20px_rgba(255,255,255,0.15)]">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/40">X / TWITTER</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl leading-none">@SYNDAFAM</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">Real-time updates, hot takes, drop announcements, late-night thoughts.</p>
                </div>
                <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-white/40">x.com/syndafam</span>
                  <span className="font-display text-sm sm:text-base rounded-full bg-white group-hover:bg-white/90 text-black px-5 py-2.5 inline-flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(255,255,255,0.4)] group-hover:-translate-y-px">
                    FOLLOW ON X <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>

            {/* DISCORD */}
            <a
              href="https://discord.com/invite/zbaAxtqCv"
              target="_blank"
              rel="noreferrer"
              className="group block relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card h-full"
              data-testid="discord-cta"
            >
              <div className="absolute inset-0 diagonal-stripes opacity-25 pointer-events-none" />
              <div className="orb pointer-events-none" style={{ width: 260, height: 260, top: -70, right: -50, background: "radial-gradient(circle, rgba(88,101,242,0.9), transparent 70%)", opacity: 0.4 }} />
              <div className="relative p-7 sm:p-8 flex flex-col h-full gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-[#5865F2] flex items-center justify-center shadow-[0_0_24px_rgba(88,101,242,0.55)]">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 fill-white" aria-hidden="true">
                      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.2.358-.43.84-.59 1.226a18.27 18.27 0 0 0-5.487 0A12.6 12.6 0 0 0 9.882 3a19.74 19.74 0 0 0-3.76 1.369C2.692 9.07 1.79 13.65 2.24 18.166a19.93 19.93 0 0 0 6.07 3.07c.49-.67.93-1.39 1.3-2.15a12.86 12.86 0 0 1-2.05-.99c.17-.13.34-.26.5-.4a14.2 14.2 0 0 0 11.88 0c.17.14.33.27.5.4-.66.39-1.34.72-2.05.99.37.76.81 1.48 1.3 2.15a19.9 19.9 0 0 0 6.07-3.07c.53-5.25-.9-9.79-3.94-13.797ZM8.02 15.331c-1.18 0-2.157-1.08-2.157-2.41 0-1.33.96-2.41 2.157-2.41 1.2 0 2.17 1.09 2.157 2.41 0 1.33-.96 2.41-2.157 2.41Zm7.96 0c-1.18 0-2.157-1.08-2.157-2.41 0-1.33.96-2.41 2.157-2.41 1.2 0 2.17 1.09 2.157 2.41 0 1.33-.95 2.41-2.157 2.41Z" />
                    </svg>
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/40">DISCORD</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl leading-none">SYNDAFAM</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">Pull up to the server — chat with the fam, drops first, sneak peeks and listening parties.</p>
                </div>
                <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-white/40">discord.gg/syndafam</span>
                  <span className="font-display text-sm sm:text-base rounded-full bg-[#5865F2] group-hover:bg-[#4752C4] text-white px-5 py-2.5 inline-flex items-center gap-2 transition-all shadow-[0_0_24px_rgba(88,101,242,0.55)] group-hover:-translate-y-px">
                    JOIN SERVER <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>

            {/* TIKTOK */}
            <a
              href="https://www.tiktok.com/@syndafam"
              target="_blank"
              rel="noreferrer"
              className="group block relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card h-full"
              data-testid="tiktok-cta"
            >
              <div className="absolute inset-0 diagonal-stripes opacity-25 pointer-events-none" />
              <div className="orb pointer-events-none" style={{ width: 260, height: 260, top: -70, right: -60, background: "radial-gradient(circle, rgba(255,20,89,0.85), transparent 70%)", opacity: 0.35 }} />
              <div className="orb pointer-events-none" style={{ width: 220, height: 220, bottom: -60, left: -60, background: "radial-gradient(circle, rgba(37,244,238,0.85), transparent 70%)", opacity: 0.3 }} />
              <div className="relative p-7 sm:p-8 flex flex-col h-full gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-black border border-white/20 flex items-center justify-center shadow-[0_0_24px_rgba(255,20,89,0.4)]">
                    <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden="true">
                      <path fill="#25F4EE" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.62a8.16 8.16 0 0 0 4.77 1.53V6.69Z" transform="translate(-1 0)" />
                      <path fill="#FE2C55" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.62a8.16 8.16 0 0 0 4.77 1.53V6.69Z" transform="translate(1 0)" />
                      <path fill="#FFFFFF" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.62a8.16 8.16 0 0 0 4.77 1.53V6.69Z" />
                    </svg>
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/40">TIKTOK</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl leading-none">@SYNDAFAM</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">Shorts, clips, dance challenges and viral moments. Follow the fam on the for-you page.</p>
                </div>
                <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-white/40">tiktok.com/@syndafam</span>
                  <span className="font-display text-sm sm:text-base rounded-full bg-white group-hover:bg-white/90 text-black px-5 py-2.5 inline-flex items-center gap-2 transition-all shadow-[0_0_24px_rgba(255,20,89,0.55)] group-hover:-translate-y-px">
                    FOLLOW <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>

            {/* FACEBOOK */}
            <a
              href="https://www.facebook.com/syndafam"
              target="_blank"
              rel="noreferrer"
              className="group block relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card h-full"
              data-testid="facebook-cta"
            >
              <div className="absolute inset-0 diagonal-stripes opacity-25 pointer-events-none" />
              <div className="orb pointer-events-none" style={{ width: 280, height: 280, top: -70, left: -60, background: "radial-gradient(circle, rgba(24,119,242,0.85), transparent 70%)", opacity: 0.4 }} />
              <div className="relative p-7 sm:p-8 flex flex-col h-full gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-[#1877F2] flex items-center justify-center shadow-[0_0_24px_rgba(24,119,242,0.55)]">
                    <Facebook className="w-8 h-8 text-white fill-white" />
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/40">FACEBOOK</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl leading-none">SYNDAFAM</div>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">Show dates, event photos, longer-form updates and community posts. Give the page a like.</p>
                </div>
                <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-white/40">facebook.com/syndafam</span>
                  <span className="font-display text-sm sm:text-base rounded-full bg-[#1877F2] group-hover:bg-[#0F63D0] text-white px-5 py-2.5 inline-flex items-center gap-2 transition-all shadow-[0_0_24px_rgba(24,119,242,0.55)] group-hover:-translate-y-px">
                    LIKE PAGE <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="font-mono text-[10px] tracking-[0.4em] text-white/40 mb-3">// THE SOCIALS</div>
          <h2 className="font-display text-4xl mb-6">FIND US OUT THERE.</h2>
          <div className="grid grid-cols-2 gap-3">
            {SOCIAL.map(({ Icon, label, url, handle, color }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="glass-card glass-hover rounded-2xl p-5 group"
                data-testid={`social-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                <Icon className="w-6 h-6 mb-3" style={{ color }} />
                <div className="font-display text-lg">{label}</div>
                <div className="font-mono text-xs text-white/50 mt-1 group-hover:text-white">{handle} →</div>
              </a>
            ))}
          </div>

          <div className="mt-10 space-y-4 font-mono text-sm">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-[#FCA311]" />
              <div>
                <div className="text-white/40 text-[10px] tracking-widest">EMAIL</div>
                <a href="mailto:Syndafambears@gmail.com" className="hover:text-[#E63946]" data-testid="connect-email">Syndafambears@gmail.com</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#FCA311]" />
              <div>
                <div className="text-white/40 text-[10px] tracking-widest">HQ</div>
                <div>Operating worldwide. Based out the basement.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="glass-card-strong ai-border rounded-3xl p-8 float-card">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-2">// SEND A MESSAGE</div>
            <h2 className="font-display text-4xl mb-6">SAY WHAT&apos;S GOOD.</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/50">NAME</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-1 bg-black/40 rounded-xl border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/50">EMAIL</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full mt-1 bg-black/40 rounded-xl border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/50">SUBJECT</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="booking / press / collab / fan mail"
                  className="w-full mt-1 bg-black/40 rounded-xl border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/50">MESSAGE</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full mt-1 bg-black/40 rounded-xl border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] resize-none transition-colors"
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="font-display tracking-wide text-lg rounded-2xl bg-[#E63946] hover:bg-[#C92A36] disabled:opacity-50 text-white px-8 py-4 inline-flex items-center gap-3 transition-colors shadow-[0_0_32px_rgba(230,57,70,0.55)]"
                data-testid="contact-submit"
              >
                <Send className="w-4 h-4" /> {loading ? "SENDING..." : "SEND IT"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
